/**
 * Players Domain
 * Types, store, and handlers for player management (real game data)
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import i18n from '../i18n/config';
import { handlePlayerGone, handlePlayerArrived } from './section-presence';
import { handleTeammateCursorGone } from './section-cursor';
import { handleTeammateHoversGone } from './cosign';
import { getViewerUserId } from './current-team';
import type { Team } from './teams';
import { syncHelpRequestFromServer } from './help-request';

// ==================== TYPES ====================

/**
 * Online player data interface (from server)
 * Represents a player currently connected to the match
 */
export interface OnlinePlayerData {
  user_id: string;
  username: string;
  display_name: string;
  session_id: string;
  joined_at: number;
  cohort_id: string;
  team_id: number;
  nakama_custom_id: string; // LiveKit participant identity
  role: string; // "student" or "moderator"
  country_code: string; // ISO 3166-1 alpha-2 country code
  timezone: string; // GMT offset, e.g. "+3", "-5", "+0"
  is_in_private_chat: boolean; // Whether player is in a private moderation chat
  help_request_type?: string; // Type of help request: "individual", "cohort", or ""
  help_requested_at?: number; // Unix timestamp when help was requested
  avatar_photo_url?: string; // Public URL of the player's profile photo; empty/missing falls back to the default avatar
  current_section?: string; // Which game section the player is viewing (e.g., "globe", "fleet")
  knowledge_points?: number; // Player's spendable KP balance (per-campaign, sourced from portal)
}

/** A player's name as the game shows it: the name they chose, or their username. */
export function playerName(player: Pick<OnlinePlayerData, 'display_name' | 'username'>): string {
  return player.display_name || player.username;
}

/**
 * A team's players online now. A teammate who has dropped stays on the roster,
 * marked disconnected, through the reconnect grace, but the server counts them
 * out at once — of a vote's majority, of a research's pool — so the client
 * does too.
 */
export function teamPlayersOnline<T extends { user_id: string; cohort_id?: string }>(
  players: Readonly<Record<string, T>>,
  disconnected: ReadonlySet<string>,
  cohortId: string | null | undefined,
): T[] {
  if (!cohortId) return [];
  return Object.values(players).filter((player) => player.cohort_id === cohortId && !disconnected.has(player.user_id));
}

/** A player's profile photo — or none, for the silhouette, when they have not set one (the server sends an empty string). */
export function playerPhoto(player: Pick<OnlinePlayerData, 'avatar_photo_url'> | undefined): string | undefined {
  return player?.avatar_photo_url || undefined;
}

/**
 * A teammate's name, or "a teammate" in the player's language while the
 * client has no record of them — one who is on a button or did something an
 * instant before their record arrived.
 */
export function teammateName(player: Pick<OnlinePlayerData, 'display_name' | 'username'> | undefined): string {
  return player ? playerName(player) : i18n.t('common.teammate');
}

/** A player's name as a screen reader says it: a moderator's role after the name. */
export function spokenPlayerName(player: Pick<OnlinePlayerData, 'display_name' | 'username' | 'role'>): string {
  return player.role === 'moderator' ? `${playerName(player)}, ${i18n.t('rail.player.state.moderator')}` : playerName(player);
}

/** People as faces, in the order given: who, and their photo — none for the silhouette. */
export function facesOf(
  userIds: readonly string[],
  players: Readonly<Record<string, Pick<OnlinePlayerData, 'avatar_photo_url'>>>,
): { id: string; photo?: string }[] {
  return userIds.map((id) => ({ id, photo: playerPhoto(players[id]) }));
}

// ==================== STORE ====================

interface KnownPlayerEntry {
  user_id: string;
  display_name: string;
  username: string;
  nakama_custom_id: string;
}

interface PlayersState {
  playersData: Record<string, OnlinePlayerData>; // Map of user_id -> OnlinePlayerData (online only)
  /** Never cleared — used for name lookups even after a player has left the match. */
  knownPlayers: Record<string, KnownPlayerEntry>;
  budgets: Record<string, number>; // Map of user_id -> budget (millions), set by server
  /** Players currently in the disconnected grace period (dimmed in UI). */
  disconnectedPlayers: Set<string>;
  /**
   * The server has announced the viewer's own join: the last of what the join
   * sends at once (players.go, held by join_snapshot_order_test.go), so the
   * join's snapshot is in. The game draws its page only from then
   * (game-screen.tsx), the loading look until.
   */
  joinLanded: boolean;
  setPlayersData: (playersData: Record<string, OnlinePlayerData>) => void;
  /** Adds or updates a player; `own` — the viewer's own join (handlePlayerJoined) — lands the join. */
  addPlayer: (playerData: OnlinePlayerData, own: boolean) => void;
  removePlayer: (userId: string) => void;
  setDisconnected: (userId: string) => void;
  updateBudgets: (budgets: Record<string, number>) => void;
  replaceBudgets: (budgets: Record<string, number>) => void;
  updateKnowledgePoints: (userId: string, newKnowledgePoints: number) => void;
  reset: () => void;
}

const initialState = {
  playersData: {},
  knownPlayers: {} as Record<string, KnownPlayerEntry>,
  budgets: {} as Record<string, number>,
  disconnectedPlayers: new Set<string>(),
  joinLanded: false,
};

/**
 * Players Store
 * Manages player state for the current match
 */
export const usePlayersStore = create<PlayersState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Set complete players data (replaces existing)
       */
      setPlayersData: (playersData) => {
        const playersList = Object.values(playersData);
        console.log('[PlayersStore] Setting players data:', Object.keys(playersData).length, 'players');
        console.log('[PlayersStore] Players:', playersList.map(p => p.display_name));

        // Merge into knownPlayers so names persist after players leave.
        const known: Record<string, KnownPlayerEntry> = { ...get().knownPlayers };
        playersList.forEach((p) => {
          known[p.user_id] = { user_id: p.user_id, display_name: p.display_name, username: p.username, nakama_custom_id: p.nakama_custom_id };
        });

        set({ playersData, knownPlayers: known }, false, 'setPlayersData');
        
        // Force log after set to verify
        console.log('[PlayersStore] State after set:', { 
          playersCount: Object.keys(playersData).length,
          playerIds: Object.keys(playersData)
        });
      },

      /**
       * Add or update a player. The viewer's own is the end of their join's
       * snapshot (`joinLanded`), as its handler has noticed.
       */
      addPlayer: (playerData, own) => {
        console.log('[PlayersStore] Adding player:', playerData.display_name);
        set(
          (state) => {
            const newDisconnected = new Set(state.disconnectedPlayers);
            newDisconnected.delete(playerData.user_id);
            return {
              joinLanded: state.joinLanded || own,
              playersData: {
                ...state.playersData,
                [playerData.user_id]: playerData,
              },
              knownPlayers: {
                ...state.knownPlayers,
                [playerData.user_id]: { user_id: playerData.user_id, display_name: playerData.display_name, username: playerData.username, nakama_custom_id: playerData.nakama_custom_id },
              },
              disconnectedPlayers: newDisconnected,
            };
          },
          false,
          'addPlayer'
        );
      },

      /**
       * Remove a player entirely (after grace period expires)
       */
      removePlayer: (userId) => {
        console.log('[PlayersStore] Removing player:', userId);
        set(
          (state) => {
            const newPlayersData = { ...state.playersData };
            delete newPlayersData[userId];
            const newDisconnected = new Set(state.disconnectedPlayers);
            newDisconnected.delete(userId);
            return { playersData: newPlayersData, disconnectedPlayers: newDisconnected };
          },
          false,
          'removePlayer'
        );
      },

      /**
       * Mark a player as disconnected (dimmed in UI, still visible)
       */
      setDisconnected: (userId) => {
        console.log('[PlayersStore] Player disconnected:', userId);
        set(
          (state) => {
            const newDisconnected = new Set(state.disconnectedPlayers);
            newDisconnected.add(userId);
            return { disconnectedPlayers: newDisconnected };
          },
          false,
          'setDisconnected'
        );
      },

      /**
       * Update budgets from server (merges into existing budgets map).
       * Used for incremental updates after purchases.
       */
      updateBudgets: (budgets: Record<string, number>) => {
        set(
          (state) => ({
            budgets: { ...state.budgets, ...budgets },
          }),
          false,
          'updateBudgets'
        );
      },

      /**
       * Replace budgets from server (full snapshot, discards stale entries).
       * Used for initial state and meeting refresh.
       */
      replaceBudgets: (budgets: Record<string, number>) => {
        set({ budgets }, false, 'replaceBudgets');
      },

      /**
       * Update a single player's Knowledge Points (used by the innovation unlock
       * success handler to apply per-payer copay deductions in real time).
       */
      updateKnowledgePoints: (userId: string, newKnowledgePoints: number) => {
        set(
          (state) => {
            const existing = state.playersData[userId];
            if (!existing) return state;
            return {
              playersData: {
                ...state.playersData,
                [userId]: { ...existing, knowledge_points: newKnowledgePoints },
              },
            };
          },
          false,
          'updateKnowledgePoints',
        );
      },

      /**
       * Reset the players store to initial state
       */
      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'players-store',
    }
  )
);

/**
 * Names a teammate by id, even one who has left the match: from the players
 * online, then from everyone the match has known, and "a teammate" for an id
 * the client has no record of yet (teammateName). Wherever the team's doings
 * name their people — the activity feed, the cities put forward on the globe,
 * the ballots, who has earned an achievement — one who left mid-activity is
 * named alike.
 */
export function useTeammateName(): (userId: string | undefined) => string {
  const players = usePlayersStore((s) => s.playersData);
  const known = usePlayersStore((s) => s.knownPlayers);
  return (userId) => teammateName(userId === undefined ? undefined : (players[userId] ?? known[userId]));
}

// ==================== THE VIEWER'S OWN JOIN ====================

/** Rejoins waiting for the viewer's own join (expectOwnJoin). */
const ownJoinWaiters = new Set<() => void>();

/** A wait for the viewer's own join: `landed` settles as it is handled; `forget` stops waiting. */
export interface OwnJoinWait {
  landed: Promise<void>;
  forget: () => void;
}

/**
 * Waits for the server to announce the viewer's own join, the last of what it
 * sends a player joining its match (players.go, HandlePlayerJoin;
 * join_snapshot_order_test.go holds the order) — only a team activity's
 * state, held to the end of the server's tick, comes after it. Armed before
 * the join, so nothing of the snapshot comes first. A rejoin waits on it
 * (reconnect-service.ts): until it lands the screen shows the game as it
 * stood before the drop.
 */
export function expectOwnJoin(): OwnJoinWait {
  let landed!: () => void;
  const promise = new Promise<void>((resolve) => {
    landed = resolve;
  });
  ownJoinWaiters.add(landed);
  return { landed: promise, forget: () => ownJoinWaiters.delete(landed) };
}

// ==================== HANDLERS ====================

/**
 * Handle OpCodePlayerJoined message
 * Adds a new player to the match. The viewer's own join is noticed here,
 * once, against the session the player signed in with (getViewerUserId): it
 * ends their join's snapshot, which the game's page waits on (joinLanded) and
 * so does a rejoin (expectOwnJoin). Its handler runs as the message is
 * routed, in the order messages arrive, so every message before it is in.
 */
export function handlePlayerJoined(data: { online_player_data: OnlinePlayerData }) {
  const player = data.online_player_data;
  const viewer = getViewerUserId();
  const own = viewer !== undefined && player.user_id === viewer;
  console.log('[PlayersHandler] Player joined:', player.display_name);
  usePlayersStore.getState().addPlayer(player, own);
  // Seed section presence so existing teammates see the new player's badge immediately
  handlePlayerArrived(player.user_id, player.current_section || 'globe');
  if (own) {
    const waiting = [...ownJoinWaiters];
    ownJoinWaiters.clear();
    waiting.forEach((landed) => landed());
  }
}

/**
 * Handle OpCodePlayersList message
 * Sets the complete list of players (sent to newly joined player)
 */
export function handlePlayersList(data: { online_players_data: Record<string, OnlinePlayerData>; teams?: Record<string, Team>; player_display_names?: Record<string, string> }) {
  const playerCount = Object.keys(data.online_players_data).length;
  console.log('[PlayersHandler] Setting players list:', playerCount, 'players');
  
  // Log each player
  Object.entries(data.online_players_data).forEach(([userId, player]) => {
    console.log('[PlayersHandler]   - Player:', {
      userId,
      username: player.username,
      display_name: player.display_name,
    });
  });
  
  usePlayersStore.getState().setPlayersData(data.online_players_data);

  // Seed knownPlayers from the server-persisted display names map so we have
  // names for players who participated in previous sessions.
  if (data.player_display_names) {
    const store = usePlayersStore.getState();
    Object.entries(data.player_display_names).forEach(([userId, displayName]) => {
      // Only add if not already in knownPlayers (setPlayersData above already seeded online players)
      if (!store.knownPlayers[userId]) {
        // Directly patch knownPlayers for historical players we only have a name for
        usePlayersStore.setState((s) => ({
          knownPlayers: {
            ...s.knownPlayers,
            [userId]: { user_id: userId, display_name: displayName, username: '', nakama_custom_id: '' },
          },
        }));
      }
    });
  }
  
  // The player's own help request, as the server holds it: a rejoin starts
  // the player afresh there, so a request held before the drop goes.
  const currentUserId = getViewerUserId();
  if (currentUserId) {
    const currentPlayerData = data.online_players_data[currentUserId];
    if (currentPlayerData) {
      syncHelpRequestFromServer(currentPlayerData.help_request_type);
    }
  }
}

/**
 * Whether a message says the viewer has gone. It cannot be so — the viewer is
 * here to be told — so it is stale, whatever sent it: before the server's
 * fixes, a grace that ended for a player back online, or a reloaded page's old
 * session leaving after the new one joined (budget_grace_test.go). Taken as
 * true it would take the viewer's own record away, and with it their team:
 * their airline drawn as a rival's. A backstop: the server sends neither now.
 */
function aboutTheViewer(userId: string, what: string): boolean {
  if (userId !== getViewerUserId()) return false;
  console.warn(`[PlayersHandler] Ignoring ${what} for the viewer, who is here`);
  return true;
}

/**
 * Handle OpCodePlayerDisconnected message
 * Marks a player as disconnected (dimmed in UI) during the grace period
 */
export function handlePlayerDisconnected(data: { user_id: string }) {
  if (aboutTheViewer(data.user_id, 'a disconnect')) return;
  console.log('[PlayersHandler] Player disconnected:', data.user_id);
  usePlayersStore.getState().setDisconnected(data.user_id);
}

/**
 * Handle a presence leaving the match — the socket's own event, before the
 * server's player_disconnected: the player dimmed, unless the session that
 * left is not the one they are here on. A reloaded page's old session leaving
 * after the new one joined says nothing of the player, teammate or viewer.
 */
export function handlePresenceLeave(presence: { user_id: string; session_id: string }) {
  const current = usePlayersStore.getState().playersData[presence.user_id]?.session_id;
  if (current !== undefined && current !== presence.session_id) {
    console.log('[PlayersHandler] Ignoring the leave of an old session of', presence.user_id);
    return;
  }
  handlePlayerDisconnected({ user_id: presence.user_id });
}

/**
 * Handle OpCodePlayerLeft message
 * Removes a player from the match (grace period expired)
 */
export function handlePlayerLeft(data: { user_id: string }) {
  if (aboutTheViewer(data.user_id, 'a player_left')) return;
  console.log('[PlayersHandler] Player left:', data.user_id);
  usePlayersStore.getState().removePlayer(data.user_id);
  handlePlayerGone(data.user_id);
  handleTeammateCursorGone(data.user_id);
  handleTeammateHoversGone(data.user_id);
}

/**
 * A player's knowledge points as another domain's message carries them: a
 * research's split charged each player on it its share (innovation.ts).
 */
export function handleKnowledgePointsUpdate(userId: string, knowledgePoints: number): void {
  usePlayersStore.getState().updateKnowledgePoints(userId, knowledgePoints);
}

// ==================== BUDGET HANDLERS ====================

/**
 * Handle OpCodeBudgetState message
 * Sets all budgets (sent to newly joined player)
 */
export function handleBudgetState(data: { budgets: Record<string, number> }) {
  console.log('[PlayersHandler] Budget state received:', Object.keys(data.budgets).length, 'entries');
  usePlayersStore.getState().replaceBudgets(data.budgets);
}

/**
 * Handle OpCodeBudgetUpdate message
 * Updates budgets after a purchase (sent to team members)
 */
export function handleBudgetUpdate(data: { budgets: Record<string, number> }) {
  usePlayersStore.getState().updateBudgets(data.budgets);
}

/**
 * Handle OpCodeBudgetRefresh message
 * Full budget reallocation at meeting start (sent to team members)
 */
export function handleBudgetRefresh(data: { budgets: Record<string, number> }) {
  console.log('[PlayersHandler] Budget refresh received');
  usePlayersStore.getState().replaceBudgets(data.budgets);
}
